import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardBody className="flex flex-col items-center gap-4 text-center">
          {children}
        </CardBody>
      </Card>
    </div>
  );
}

export interface AuthAction {
  label: string;
  onClick: () => void;
}

/**
 * «Входим…» — промежуточный экран, пока идёт переход на signaris-auth.
 *
 * До 0.12 на этом месте лоадеры бросали `Response` 302 без `Location`, то есть
 * дефолтный экран ошибки роутера: он лишь мелькал, потому что браузер тут же
 * уезжал на auth. С 0.12 вход из скрытой страницы (фоновая вкладка, свёрнутое
 * окно, prerender) откладывается до показа — экран ошибки висел бы всё это
 * время. Спиннер может жить минутами, это норма.
 */
export function AuthPending({
  text = "Входим…",
  action,
}: {
  text?: string;
  action?: AuthAction | null;
}) {
  return (
    <Shell>
      <Loader2 size={28} className="animate-spin text-accent" />
      <div className="text-sm text-fg-muted">{text}</div>
      {action && (
        <Button variant="outline" size="sm" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </Shell>
  );
}

/**
 * Терминальный экран с кнопкой. Техническое сообщение ошибки здесь НЕ
 * показываем: с 0.12 это может быть английское «no PKCE verifier for this
 * login attempt». Свой русский текст — на экран, техническое — в консоль,
 * машинный код (HTTP-статус) — в `detail`.
 */
export function AuthNotice({
  icon,
  title,
  description,
  detail,
  primary,
  secondary,
}: {
  icon: ReactNode;
  title: string;
  description: ReactNode;
  detail?: string | null;
  primary: AuthAction;
  secondary?: AuthAction;
}) {
  return (
    <Shell>
      {icon}
      <div className="font-display text-lg font-semibold tracking-tight">
        {title}
      </div>
      <p className="text-sm text-fg-muted">{description}</p>
      {detail && <p className="text-xs text-fg-subtle">{detail}</p>}
      <div className="flex flex-col sm:flex-row gap-2 pt-2">
        <Button onClick={primary.onClick}>{primary.label}</Button>
        {secondary && (
          <Button variant="outline" onClick={secondary.onClick}>
            {secondary.label}
          </Button>
        )}
      </div>
    </Shell>
  );
}
